import './ThreeScene.css'
import React, { useEffect, useRef, useState } from 'react'
import * as THREE from 'three'
import {
  createDraggableCargoPlaceholder,
  isPlaceholder,
  readMouseMove,
  catch_draggable,
} from './ThreeFunctionsJS/dragObjectsFunctions'
import {
  createSceneAndLight,
  createAndAddingGroups,
  createCamera,
  createRenderer,
  createControls,
  clearScene,
  setupArrowKeyScroll
} from './ThreeFunctionsJS/sceneInitialization'
import { cargo_area_adding } from './ThreeFunctionsJS/cargoAreaFunctions'
import { 
  createCargos, 
  clearAllCargos, 
  disposeCargoResources,
  updateCargoPosition // Make sure this is imported
} from './ThreeFunctionsJS/cargoFuctions'
import { disposeContainerTextures } from './ThreeFunctionsJS/cargoAreaImageConteiner';

const ThreeScene = ( {cargos, setCargos, cargoArea, isCreated, 
  // onMessage,
  onCameraReady,    // ADD THIS
  onControlsReady   // ADD THIS
 } ) => {
  const mountRef = useRef(null)
  const draggable_cargo = useRef('')
  const backup_draggable_cargo = useRef('')
  const cameraRef = useRef(null)
  const [cameraState, setCameraState] = useState(null)

  // Use refs instead of state for drag tracking
  const sceneRef = useRef(null)
  const controlsRef = useRef(null)
  const rendererRef = useRef(null)
  const cargoGroupRef = useRef(null)
  const isDraggingRef = useRef(false)
  const lastCargosRef = useRef([]) // Track the last cargos state

  // Track if scene is initialized
  const [isInitialized, setIsInitialized] = useState(false)

  useEffect(() => {
    // Only initialize if isCreated is true and scene is not already initialized
    if (!isCreated || isInitialized) return;

    console.log('🚀 Initializing scene...');

    // Scene initialization
    let scene = createSceneAndLight()
    sceneRef.current = scene
    
    let camera = createCamera( cargoArea )
    cameraRef.current = camera
    
    if (cameraState) {
      camera.position.set(cameraState.x, cameraState.y, cameraState.z)
    }

    let renderer = createRenderer()
    rendererRef.current = renderer

    const canvas = renderer.domElement;

    // Initialize OrbitControls
    let controls = createControls(camera, renderer)
    controlsRef.current = controls

    // NOTIFY PARENT ABOUT CAMERA AND CONTROLS
    if (onCameraReady) onCameraReady(camera);
    if (onControlsReady) onControlsReady(controls);

    // Make renderer focusable
    canvas.setAttribute('tabindex', '0')
    canvas.style.outline = 'none'

    // Setup arrow key scrolling
    const cleanupArrowScroll = setupArrowKeyScroll(camera, controls, renderer)

    // Adding groups to scene
    let [
      cargo_area_group,
      cargo_group,
      group_of_cargo_area_floor,
      group_of_cargo_area_attribute,
    ] = createAndAddingGroups()
    
    cargoGroupRef.current = cargo_group;
    
    scene.add(cargo_area_group)
    scene.add(cargo_group)
    scene.add(group_of_cargo_area_floor)
    scene.add(group_of_cargo_area_attribute)
    
    if (mountRef.current) {
      mountRef.current.innerHTML = ''
      mountRef.current.appendChild(renderer.domElement)
    }

    // Raycaster for mouse interactions
    let raycaster = new THREE.Raycaster()
    let moveMouse = new THREE.Vector2()
    let clickMouse = new THREE.Vector2()
    draggable_cargo.current = createDraggableCargoPlaceholder()
    backup_draggable_cargo.current = createDraggableCargoPlaceholder()
    
    let canvasBounds = canvas.getBoundingClientRect()

    // Timing for drag detection
    let start_time = new Date()

    let onMouseMove = (event) => readMouseMove(event, renderer, moveMouse)
    
    let onMouseDown = (event) => {
      start_time = new Date();
      isDraggingRef.current = true;
    }
    
    // Fixed onMouseUp - handle everything locally
    let onMouseUp = (event) => {
      const end_time = new Date();
      const timeDiff = end_time - start_time;
      
      if (timeDiff < 200) {
        console.log('🔵 Quick click - picking object');
        
        // Call catch_draggable directly with all necessary parameters
        catch_draggable(
          controls,
          cargo_group,
          clickMouse,
          canvasBounds,
          raycaster,
          camera,
          scene,
          draggable_cargo,
          backup_draggable_cargo,
          cargos,
          setCargos,
          // onMessage
        );
      
      console.log('🔵 catch_draggable called with cargos:', cargos);
      console.log('🔵 cargos length:', cargos.length);      }
      isDraggingRef.current = false;
    }

    // Also need to handle click position for object selection
    let onClick = (event) => {
      clickMouse.x = ((event.clientX - canvasBounds.left) / (canvasBounds.right - canvasBounds.left)) * 2 - 1;
      clickMouse.y = -((event.clientY - canvasBounds.top) / (canvasBounds.bottom - canvasBounds.top)) * 2 + 1;
    }

    canvas.addEventListener('mousemove', onMouseMove);
    canvas.addEventListener('mousedown', onMouseDown);
    canvas.addEventListener('mouseup', onMouseUp);
    canvas.addEventListener('click', onClick);

    // Drag object function
    function dragObject() {
      raycaster.setFromCamera(moveMouse, camera)
      if (!isPlaceholder(draggable_cargo.current)) {
        let array_of_floors_for_draggable = [
          ...group_of_cargo_area_floor.children,
          ...cargo_group.children,
        ]

        const cargos_on_ray = raycaster.intersectObjects(
          array_of_floors_for_draggable,
          false
        )

        if (cargos_on_ray.length > 0) {
          let found = cargos_on_ray[0]

          if (found.faceIndex == 4 || found.faceIndex == 5) {
            // X axis constraints
            if (
              found.point.x <=
              Number(draggable_cargo.current.geometry.parameters.width) / 2
            ) {
              draggable_cargo.current.position.x =
                Number(draggable_cargo.current.geometry.parameters.width) / 2
            } else if (
              found.point.x +
                Number(draggable_cargo.current.geometry.parameters.width) / 2 >=
              cargo_area_group.children[0].scale.x * 2
            ) {
              draggable_cargo.current.position.x =
                cargo_area_group.children[0].scale.x * 2 -
                Number(draggable_cargo.current.geometry.parameters.width) / 2
            } else {
              draggable_cargo.current.position.x = found.point.x
            }

            // Z axis constraints
            if (
              found.point.z <=
              Number(draggable_cargo.current.geometry.parameters.depth) / 2
            ) {
              draggable_cargo.current.position.z =
                Number(draggable_cargo.current.geometry.parameters.depth) / 2
            } else if (
              found.point.z +
                Number(draggable_cargo.current.geometry.parameters.depth) / 2 >=
              cargo_area_group.children[0].scale.z * 2
            ) {
              draggable_cargo.current.position.z =
                cargo_area_group.children[0].scale.z * 2 -
                Number(draggable_cargo.current.geometry.parameters.depth) / 2
            } else {
              draggable_cargo.current.position.z = found.point.z
            }

            draggable_cargo.current.position.y =
              found.point.y +
              Number(draggable_cargo.current.geometry.parameters.height) / 2
          }
        }
      }
    }
    

    // Create cargo area
    if (cargoArea && cargoArea.length > 0 ) {
      cargo_area_adding(
        cargoArea,
        cargo_area_group,
        group_of_cargo_area_attribute,
        group_of_cargo_area_floor,
        camera,
        controls,
        cameraState
      )
    }
    
    // Create initial cargos
    const activeCargos = cargos.filter(cargo => cargo.active && cargo.visible);
    if (activeCargos.length > 0) {
      createCargos(activeCargos, cargo_group)
    }

    // Store initial cargos state
    lastCargosRef.current = [...cargos];

    // Animation loop
    let animationFrameId






    function animate() {
      controls.update();
      dragObject();

// function diagnoseScene() {
//   console.log('=== Scene Diagnostics ===');
//   console.log('Total children:', scene.children[2].children.length);
  
//   const meshes = [];
//   const helpers = [];
//   const others = [];
  
//   scene.children[2].children.forEach(child => {
//     if (child.isMesh) meshes.push(child);
//     else if (child.isAxesHelper || child.isGridHelper || child.isCameraHelper) helpers.push(child);
//     else others.push(child);
//   });
  
//   console.log('Meshes:', meshes.length);
//   console.log('Helpers:', helpers.length);
//   console.log('Others:', others.length);
  
//   meshes.forEach((mesh, i) => {
//     console.log(`Mesh ${i}:`, {
//       name: mesh.name,
//       visible: mesh.visible,
//       position: mesh.position.toArray(),
//       scale: mesh.scale.toArray(),
//       geometryVertices: mesh.geometry.attributes.position.count / 3,
//       material: mesh.material.type,
//       opacity: mesh.material.opacity,
//       transparent: mesh.material.transparent,
//       depthTest: mesh.material.depthTest,
//       depthWrite: mesh.material.depthWrite,
//       color: mesh.material.color    });
//   });
  
//   // Check if any mesh has zero vertices
//   const zeroVertexMeshes = meshes.filter(mesh => 
//     mesh.geometry.attributes.position.count === 0
//   );
  
//   if (zeroVertexMeshes.length > 0) {
//     console.log('Warning: Meshes with zero vertices:', zeroVertexMeshes.length);
//   }
// }
// diagnoseScene();



      renderer.render(scene, camera);
      animationFrameId = requestAnimationFrame(animate);
    }

    animate()

    setIsInitialized(true);

    // Cleanup
    return () => {
      cancelAnimationFrame(animationFrameId);

      if (cleanupArrowScroll) {
        cleanupArrowScroll()
      }

      
      clearAllCargos(cargoGroupRef.current)
      disposeCargoResources()
      disposeContainerTextures()

      

      // Remove event listeners from canvas
      if (canvas) {
        canvas.removeEventListener('mousemove', onMouseMove)
        canvas.removeEventListener('mousedown', onMouseDown)
        canvas.removeEventListener('mouseup', onMouseUp)
        canvas.removeEventListener('click', onClick)
      }

      if (renderer) {
        renderer.dispose()
      }
      
      if (mountRef.current && canvas) {
        mountRef.current.removeChild(canvas)
      }
      
      // Clear refs
      cargoGroupRef.current = null;
      sceneRef.current = null;
      controlsRef.current = null;
      rendererRef.current = null;
      isDraggingRef.current = false;
      setIsInitialized(false);

      // Notify parent that camera and controls are no longer available
      if (onCameraReady) onCameraReady(null);
      if (onControlsReady) onControlsReady(null);      
    }
  }, [isCreated
    // , onCameraReady, onControlsReady, cameraState
    ]) // Empty dependency - runs only once

  // Handle reset when isCreated becomes false
  useEffect(() => {
    if (!isCreated && isInitialized) {
      console.log('🔄 Resetting scene because isCreated is false');
      
      // Clear all cargos
      if (cargoGroupRef.current) {
        clearAllCargos(cargoGroupRef.current);
      }

      // Dispose resources
      disposeCargoResources();
      disposeContainerTextures();

      // Clear scene if it exists
      if (sceneRef.current) {
        clearScene(sceneRef.current);
      }

      // Reset drag objects
      draggable_cargo.current = createDraggableCargoPlaceholder();
      backup_draggable_cargo.current = createDraggableCargoPlaceholder();
      
      isDraggingRef.current = false;
      setIsInitialized(false);
    }
  }, [isCreated, isInitialized]);

  // Handle cargoArea updates
  useEffect(() => {
    if (!sceneRef.current || !cargoArea || cargoArea.length === 0) return;
    
    const cargo_area_group = sceneRef.current.children.find(child => child.name === 'cargo_area_group');
    const group_of_cargo_area_attribute = sceneRef.current.children.find(child => child.name === 'group_of_cargo_area_attribute');
    const group_of_cargo_area_floor = sceneRef.current.children.find(child => child.name === 'group_of_cargo_area_floor');
    
    if (cargo_area_group && group_of_cargo_area_attribute && group_of_cargo_area_floor) {
      cargo_area_adding(
        cargoArea,
        cargo_area_group,
        group_of_cargo_area_attribute,
        group_of_cargo_area_floor,
        cameraRef.current,
        controlsRef.current,
        cameraState
      )
    }

  }, [cargoArea]);

  // Handle cargos updates - IMPROVED VERSION
  // Handle cargos updates - IMPROVED VERSION
  useEffect(() => {
    if (!cargoGroupRef.current || !sceneRef.current || isDraggingRef.current) {
      return;
    }
    
    const activeCargos = cargos.filter(cargo => cargo.active && cargo.visible);
    
    // Check if cargos actually changed (not just reference)
    const cargosChanged = JSON.stringify(cargos) !== JSON.stringify(lastCargosRef.current);
    
    if (!cargosChanged) {
      return; // Skip if cargos didn't actually change
    }
    
    console.log('🔄 Cargos changed, updating scene...');
    
    // USE THE updateExistingCargos FUNCTION HERE
    updateExistingCargos(activeCargos, cargoGroupRef.current);
    
    // Store current cargos state for next comparison
    lastCargosRef.current = [...cargos];
    
  }, [cargos]);

  // Helper function to update existing cargos
  const updateExistingCargos = (activeCargos, cargoGroup) => {
    const existingCargosMap = new Map();
    
    // Create a map of existing cargos by UUID
    cargoGroup.children.forEach(child => {
      if (child.userData.type === 'cargo') {
        existingCargosMap.set(child.userData.cargoId, child);
      }
    });

    const processedIds = new Set();

    // Update existing cargos
    activeCargos.forEach(cargo => {
      const cargoId = cargo.uuid;
      processedIds.add(cargoId);
      
      if (existingCargosMap.has(cargoId)) {
        // Update existing cargo position
        const existingCargo = existingCargosMap.get(cargoId);
        const newPosition = { x: cargo.x, y: cargo.y, z: cargo.z };
        updateCargoPosition(existingCargo, newPosition);
      } else {
        // Create new cargo
        createCargos([cargo], cargoGroup);
      }
    });

    // Remove cargos that are no longer in activeCargos
    existingCargosMap.forEach((cargoMesh, cargoId) => {
      if (!processedIds.has(cargoId)) {
        // This will remove the cargo from the scene
        cargoGroup.remove(cargoMesh);
        // Also dispose of resources
        if (cargoMesh.geometry) cargoMesh.geometry.dispose();
        if (cargoMesh.material) {
          if (Array.isArray(cargoMesh.material)) {
            cargoMesh.material.forEach(mat => mat.dispose());
          } else {
            cargoMesh.material.dispose();
          }
        }
      }
    });
  };

  return <div className="three-scene-container" ref={mountRef} />
}

export default ThreeScene